/**
 * Navigation Manager for ZippyHealth
 * Handles safe navigation between different views and features
 */

export interface NavigationTarget {
  id: string;
  path: string;
  title: string;
  requiresAuth?: boolean;
  beta?: boolean;
}

export interface NavigationConfig {
  onViewChange?: (view: NavigationTarget) => void;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

class NavigationManager {
  private config: NavigationConfig = {};
  private currentView: NavigationTarget | null = null;
  private navigationHistory: NavigationTarget[] = [];

  // Available navigation targets
  private readonly navigationTargets: NavigationTarget[] = [
    { id: 'dashboard', path: '/dashboard', title: 'Dashboard' },
    { id: 'conditions', path: '/conditions', title: 'Health Conditions' },
    { id: 'symptoms', path: '/symptoms', title: 'Symptom Tracking' },
    { id: 'advanced-symptoms', path: '/advanced-symptoms', title: 'Advanced Symptoms' },
    { id: 'ai-docs', path: '/ai-docs', title: 'AI Document Analysis' },
    { id: 'ai-coach', path: '/ai-coach', title: 'AI Health Coach', beta: true },
    { id: 'devices', path: '/devices', title: 'Device Integration' },
    { id: 'analytics', path: '/analytics', title: 'Health Analytics' },
    { id: 'medications', path: '/medications', title: 'Medication Manager' },
    { id: 'appointments', path: '/appointments', title: 'Appointment Scheduler' },
    { id: 'export', path: '/export', title: 'Data Export' },
    { id: 'food-tracking', path: '/food-tracking', title: 'Food Tracking' },
    { id: 'enhanced-food-tracker', path: '/enhanced-food-tracker', title: 'Enhanced Food Tracker' },
    { id: 'meal-log', path: '/meal-log', title: 'Meal Log' },
    { id: 'meal-planner', path: '/meal-planner', title: 'Meal Planner' },
    { id: 'recipe-book', path: '/recipe-book', title: 'Recipe Book' },
    { id: 'guru-connect', path: '/guru-connect', title: 'Guru Connect' },
    { id: 'blockchain', path: '/blockchain', title: 'Blockchain Health' },
    { id: 'admin', path: '/admin', title: 'Admin Dashboard', requiresAuth: true }
  ];

  /**
   * Update navigation configuration
   */
  updateConfig(config: NavigationConfig): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get all available navigation targets
   */
  getNavigationTargets(): NavigationTarget[] {
    return this.navigationTargets;
  }

  /**
   * Get current view
   */
  getCurrentView(): NavigationTarget | null {
    return this.currentView;
  }

  /**
   * Get navigation history
   */
  getNavigationHistory(): NavigationTarget[] {
    return [...this.navigationHistory];
  }

  /**
   * Navigate to a specific view
   */
  async navigate(targetId: string): Promise<boolean> {
    try {
      const target = this.navigationTargets.find(t => t.id === targetId);
      
      if (!target) {
        throw new Error(`Navigation target '${targetId}' not found`);
      }

      // Check if user is authenticated for protected routes
      if (target.requiresAuth) {
        const isAuthenticated = await this.checkAuthentication();
        if (!isAuthenticated) {
          throw new Error('Authentication required for this view');
        }
      }

      // Update current view
      this.currentView = target;
      
      // Add to history
      this.navigationHistory.push(target);
      
      // Limit history size
      if (this.navigationHistory.length > 50) {
        this.navigationHistory = this.navigationHistory.slice(-50);
      }

      // Update URL
      this.updateURL(target.path);

      // Notify listeners
      this.config.onViewChange?.(target);
      this.config.onSuccess?.();

      return true;
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      this.config.onError?.(errorObj);
      return false;
    }
  }

  /**
   * Safe navigation with error handling
   */
  async safeNavigate(targetId: string): Promise<boolean> {
    try {
      return await this.navigate(targetId);
    } catch (error) {
      console.error('Navigation error:', error);
      return false;
    }
  }

  /**
   * Navigate back in history
   */
  async navigateBack(): Promise<boolean> {
    if (this.navigationHistory.length <= 1) {
      return false;
    }

    // Remove current view from history
    this.navigationHistory.pop();
    
    // Get previous view
    const previousView = this.navigationHistory[this.navigationHistory.length - 1];
    
    if (previousView) {
      this.currentView = previousView;
      this.updateURL(previousView.path);
      this.config.onViewChange?.(previousView);
      return true;
    }

    return false;
  }

  /**
   * Navigate to home/dashboard
   */
  async navigateHome(): Promise<boolean> {
    return this.navigate('dashboard');
  }

  /**
   * Check if a view is accessible
   */
  async canNavigateTo(targetId: string): Promise<boolean> {
    const target = this.navigationTargets.find(t => t.id === targetId);
    
    if (!target) {
      return false;
    }

    if (target.requiresAuth) {
      return await this.checkAuthentication();
    }

    return true;
  }

  /**
   * Get beta features
   */
  getBetaFeatures(): NavigationTarget[] {
    return this.navigationTargets.filter(t => t.beta);
  }

  /**
   * Update URL without triggering navigation
   */
  private updateURL(path: string): void {
    try {
      if (typeof window !== 'undefined' && window.history) {
        window.history.pushState({}, '', path);
      }
    } catch (error) {
      console.warn('Failed to update URL:', error);
    }
  }

  /**
   * Check if user is authenticated
   */
  private async checkAuthentication(): Promise<boolean> {
    // In a real implementation, this would check with your auth provider
    // For now, we'll assume the user is authenticated if they have a session
    try {
      const session = localStorage.getItem('auth_session');
      return !!session;
    } catch (error) {
      return false;
    }
  }

  /**
   * Clear navigation history
   */
  clearHistory(): void {
    this.navigationHistory = [];
  }

  /**
   * Get navigation statistics
   */
  getNavigationStats() {
    return {
      totalTargets: this.navigationTargets.length,
      betaFeatures: this.getBetaFeatures().length,
      historyLength: this.navigationHistory.length,
      currentView: this.currentView?.id || null
    };
  }
}

// Export singleton instance
export const navigationManager = new NavigationManager(); 